import { lazy, Suspense } from "react";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { AppShell } from "./components/layout/AppShell";
import { DataState } from "./components/common/DataState";
import { useAuth } from "./contexts/AuthContext";
import { WorkspaceProvider } from "./contexts/WorkspaceContext";
import { LoginPage } from "./features/auth/LoginPage";

const DashboardPage = lazy(() =>
    import("./features/dashboard/DashboardPage").then((module) => ({ default: module.DashboardPage })),
);
const StockPage = lazy(() => import("./features/stock/StockPage").then((module) => ({ default: module.StockPage })));
const PickingPage = lazy(() => import("./features/picking/PickingPage").then((module) => ({ default: module.PickingPage })));
const ReceiptsPage = lazy(() =>
    import("./features/receiving/ReceiptsPage").then((module) => ({ default: module.ReceiptsPage })),
);
const RequestsPage = lazy(() =>
    import("./features/requests/RequestsPage").then((module) => ({ default: module.RequestsPage })),
);
const InventoryPage = lazy(() =>
    import("./features/inventory/InventoryPage").then((module) => ({ default: module.InventoryPage })),
);
const QualityPage = lazy(() => import("./features/quality/QualityPage").then((module) => ({ default: module.QualityPage })));
const WarehousePage = lazy(() =>
    import("./features/warehouse/WarehousePage").then((module) => ({ default: module.WarehousePage })),
);
const Warehouse3DPage = lazy(() =>
    import("./features/warehouse/Warehouse3DPage").then((module) => ({ default: module.Warehouse3DPage })),
);
const CatalogPage = lazy(() => import("./features/catalog/CatalogPage").then((module) => ({ default: module.CatalogPage })));
const FormulaPage = lazy(() => import("./features/formula/FormulaPage").then((module) => ({ default: module.FormulaPage })));
const ProductionPage = lazy(() =>
    import("./features/production/ProductionPage").then((module) => ({ default: module.ProductionPage })),
);
const FefoPage = lazy(() => import("./features/reorg/FefoPage").then((module) => ({ default: module.FefoPage })));
const RolesPage = lazy(() => import("./features/roles/RolesPage").then((module) => ({ default: module.RolesPage })));
const AuditPage = lazy(() => import("./features/audit/AuditPage").then((module) => ({ default: module.AuditPage })));
const MovementsPage = lazy(() =>
    import("./features/movements/MovementsPage").then((module) => ({ default: module.MovementsPage })),
);
const StockByLotPage = lazy(() =>
    import("./features/stock/StockByLotPage").then((module) => ({ default: module.StockByLotPage })),
);
const MergePalletsPage = lazy(() =>
    import("./features/stock/MergePalletsPage").then((module) => ({ default: module.MergePalletsPage })),
);
const ManualConsumePage = lazy(() =>
    import("./features/stock/ManualConsumePage").then((module) => ({ default: module.ManualConsumePage })),
);
const GuidePage = lazy(() => import("./features/guide/GuidePage").then((module) => ({ default: module.GuidePage })));

export function App() {
    return (
        <Routes>
            <Route path="/login" element={<PublicRoute />} />
            <Route element={<RequireAuth />}>
                <Route
                    element={
                        <WorkspaceProvider>
                            <AppShell />
                        </WorkspaceProvider>
                    }
                >
                    <Route
                        element={
                            <Suspense fallback={<DataState loading />}>
                                <Outlet />
                            </Suspense>
                        }
                    >
                        <Route index element={<DashboardPage />} />
                        <Route path="stock" element={<StockPage />} />
                        <Route path="reorg" element={<FefoPage />} />
                        <Route path="receipts" element={<ReceiptsPage />} />
                        <Route path="requests" element={<RequestsPage />} />
                        <Route path="picking" element={<PickingPage />} />
                        <Route path="inventory" element={<InventoryPage />} />
                        <Route path="quality" element={<QualityPage />} />
                        <Route path="warehouse" element={<WarehousePage />} />
                        <Route path="warehouse/3d" element={<Warehouse3DPage />} />
                        <Route path="formulas" element={<FormulaPage />} />
                        <Route path="production" element={<ProductionPage />} />
                        <Route path="products" element={<CatalogPage type="products" />} />
                        <Route path="suppliers" element={<CatalogPage type="suppliers" />} />
                        <Route path="users" element={<CatalogPage type="users" />} />
                        <Route path="roles" element={<RolesPage />} />
                        <Route path="movements" element={<MovementsPage />} />
                        <Route path="stock-by-lot" element={<StockByLotPage />} />
                        <Route path="merge-pallets" element={<MergePalletsPage />} />
                        <Route path="manual-consume" element={<ManualConsumePage />} />
                        <Route path="audit" element={<AuditPage />} />
                        <Route path="guide" element={<GuidePage />} />
                    </Route>
                </Route>
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function RequireAuth() {
    const { isAuthenticated } = useAuth();
    const location = useLocation();
    return isAuthenticated ? <Outlet /> : <Navigate to="/login" state={{ from: location }} replace />;
}
function PublicRoute() {
    const { isAuthenticated } = useAuth();
    return isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />;
}
